import * as React from 'react'
import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'
import { X } from 'lucide-react'

import { cn } from '@/lib/utils'
import { Icon } from './icon'
import dynamicIconImports from 'lucide-react/dynamicIconImports'

export type IconName = keyof typeof dynamicIconImports

const badgeVariants = cva(
  'inline-flex items-center border transition-colors focus:outline-none focus:ring-1 focus:ring-ring focus:ring-offset-0',
  {
    variants: {
      variant: {
        default:
          'border-transparent bg-primary text-primary-foreground hover:bg-primary/80',
        primary:
          'border-transparent bg-primary text-primary-foreground hover:bg-primary/80',
        secondary:
          'border-transparent bg-secondary text-secondary-foreground hover:bg-secondary/80',
        success:
          'border-transparent bg-green-600 text-primary-foreground hover:bg-green-600/80',
        warning:
          'border-transparent bg-yellow-600 text-primary-foreground hover:bg-yellow-600/80',
        error:
          'border-transparent bg-destructive text-destructive-foreground hover:bg-destructive/80',
        outline: 'border-border text-foreground hover:bg-accent',
        pill: 'border-border bg-muted/60 text-muted-foreground hover:bg-muted/80',
        rounded: 'border-transparent bg-secondary text-secondary-foreground hover:bg-secondary/80',
      },
      size: {
        sm: 'h-4 px-2 text-xs',
        md: 'h-5 px-2.5 text-sm',
        lg: 'h-6 px-3 text-base',
        xl: 'h-7 px-4 text-lg',
      },
      shape: {
        rounded: 'rounded-full',
        pill: 'rounded-md',
      },
      dot: {
        true: 'pl-1.5',
      },
    },
    compoundVariants: [
      {
        variant: 'pill',
        size: 'sm',
        className: 'gap-1 px-1 py-0.5 text-xs font-normal',
      },
      {
        variant: 'pill',
        size: 'md',
        className: 'gap-1.5 px-1.5 py-0.5 text-xs font-normal',
      },
      {
        variant: 'pill',
        size: 'lg',
        className: 'gap-2 px-2 py-1 text-sm font-normal',
      },
      {
        variant: 'pill',
        size: 'xl',
        className: 'gap-2.5 px-3 py-1.5 text-base font-normal',
      },
      {
        variant: 'rounded',
        className: 'gap-1.5 px-2 py-1.5 rounded-sm cursor-pointer',
      },
    ],
    defaultVariants: {
      variant: 'default',
      size: 'md',
      shape: 'rounded',
    },
  }
)

export interface BadgeProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof badgeVariants> {
  dot?: boolean
  asChild?: boolean
  icon?: IconName
  onClose?: () => void
}

const Badge = React.forwardRef<HTMLDivElement, BadgeProps>(
  (
    {
      className,
      variant,
      size,
      shape,
      dot,
      asChild = false,
      icon,
      onClose,
      children,
      ...props
    },
    ref
  ) => {
    const Comp = asChild ? Slot : 'div'
    const isPill = variant === 'pill'
    const finalShape = isPill ? 'pill' : shape

    return (
      <Comp
        className={cn(
          badgeVariants({ variant, size, shape: finalShape, dot }),
          className
        )}
        ref={ref}
        {...props}
      >
        {dot && !isPill && (
          <span className="mr-1 flex h-2 w-2 rounded-full bg-current" />
        )}
        {icon && isPill && <Icon name={icon} className="h-3.5 w-3.5" />}
        {children}
        {onClose && isPill && (
          <button
            type="button"
            className="text-foreground ring-offset-background focus:ring-ring ml-1.5 inline-flex h-3 w-3 shrink-0 items-center justify-center rounded-full opacity-70 transition-opacity hover:opacity-100 focus:ring-1 focus:ring-offset-0 focus:outline-none disabled:pointer-events-none"
            onClick={onClose}
          >
            <X className="h-3 w-3" />
            <span className="sr-only">Close</span>
          </button>
        )}
      </Comp>
    )
  }
)

Badge.displayName = 'Badge'

export { Badge, badgeVariants }
